import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LANGUAGES } from "@/i18n";

// Inline circular flag SVGs (compact, no external deps)
export const FLAGS = {
  GB: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <defs>
        <clipPath id="gb-clip"><circle cx="30" cy="30" r="30" /></clipPath>
      </defs>
      <g clipPath="url(#gb-clip)">
        <rect width="60" height="60" fill="#012169" />
        <path d="M0 0L60 60M60 0L0 60" stroke="#FFFFFF" strokeWidth="10" />
        <path d="M0 0L60 60M60 0L0 60" stroke="#C8102E" strokeWidth="5" />
        <path d="M30 0V60M0 30H60" stroke="#FFFFFF" strokeWidth="16" />
        <path d="M30 0V60M0 30H60" stroke="#C8102E" strokeWidth="8" />
      </g>
    </svg>
  ),
  NL: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <defs>
        <clipPath id="nl-clip"><circle cx="30" cy="30" r="30" /></clipPath>
      </defs>
      <g clipPath="url(#nl-clip)">
        <rect width="60" height="20" fill="#AE1C28" />
        <rect y="20" width="60" height="20" fill="#FFFFFF" />
        <rect y="40" width="60" height="20" fill="#21468B" />
      </g>
    </svg>
  ),
  FR: (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <defs>
        <clipPath id="fr-clip"><circle cx="30" cy="30" r="30" /></clipPath>
      </defs>
      <g clipPath="url(#fr-clip)">
        <rect width="20" height="60" fill="#0055A4" />
        <rect x="20" width="20" height="60" fill="#FFFFFF" />
        <rect x="40" width="20" height="60" fill="#EF4135" />
      </g>
    </svg>
  ),
};

function useLanguage() {
  const { i18n: i18nInstance } = useTranslation();
  const lang = (i18nInstance.resolvedLanguage || i18nInstance.language || "en").slice(0, 2);
  const active = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const change = (code) => {
    i18nInstance.changeLanguage(code);
    try { localStorage.setItem("trustline_lang", code); } catch { /* ignore */ }
  };
  return { active, change };
}

export function LanguageSegment({ testid = "language-segment" }) {
  const { active, change } = useLanguage();
  return (
    <div role="group" aria-label="Language" data-testid={testid} className="inline-flex items-center rounded-full bg-slate-100 p-0.5 ring-1 ring-slate-200/80">
      {LANGUAGES.map((l) => (
        <button key={l.code} type="button" onClick={() => change(l.code)} aria-pressed={active.code === l.code} data-testid={`${testid}-${l.code}`}
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 h-8 text-xs font-semibold transition-[background-color,color] ${active.code === l.code ? "bg-white text-[#1B3A6B] shadow-sm" : "text-slate-500 hover:text-[#1B3A6B]"}`}>
          <span className="block w-4 h-4 rounded-full overflow-hidden ring-1 ring-black/10">{FLAGS[l.country]}</span>{l.short}
        </button>
      ))}
    </div>
  );
}

export default function LanguageSwitcher({ size = 28, dark = false }) {
  const { active, change } = useLanguage();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Language"
          data-testid="language-switcher"
          className={`inline-flex items-center gap-1 rounded-full p-0.5 pr-1.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5BA83F] ${dark ? "hover:bg-white/10" : "hover:bg-slate-100"}`}
        >
          <span
            className="block rounded-full overflow-hidden ring-1 ring-black/10 shadow-sm"
            style={{ width: size, height: size }}
          >
            {FLAGS[active.country]}
          </span>
          <span className={`text-xs font-bold ${dark ? "text-white" : "text-[#1B3A6B]"}`} data-testid="language-switcher-current">{active.short}</span>
          <ChevronDown className={`w-3.5 h-3.5 ${dark ? "text-white/80" : "text-slate-500"}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            onClick={() => change(l.code)}
            data-testid={`language-option-${l.code}`}
            className="flex items-center gap-2.5 cursor-pointer"
          >
            <span className="block w-5 h-5 rounded-full overflow-hidden ring-1 ring-black/10">
              {FLAGS[l.country]}
            </span>
            <span className="text-sm">{l.label}</span>
            {l.code === active.code && <span className="ml-auto text-xs text-[#5BA83F] font-semibold">●</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
